import { http } from './client';
import type { User, Admin } from '../types';

/**
 * ==========================================
 * 认证相关 API 接口
 * 提供用户和管理员的登录、注册、令牌刷新等功能
 * ==========================================
 */

/**
 * 用户登录请求参数
 */
export interface LoginRequest {
  /** 手机号码 */
  phone: string;
  /** 登录密码 */
  password: string;
  /** 邀请码（可选） */
  inviteCode?: string;
}

/**
 * 用户登录响应数据
 */
export interface LoginResponse {
  /** 访问令牌 */
  accessToken: string;
  /** 刷新令牌 */
  refreshToken: string;
  /** 令牌过期时间（秒） */
  expiresIn: number;
  /** 用户信息 */
  user: User;
}

/**
 * 管理员登录请求参数
 */
export interface AdminLoginRequest {
  /** 登录账号 */
  account: string;
  /** 登录密码 */
  password: string;
}

/**
 * 管理员登录响应数据
 */
export interface AdminLoginResponse {
  /** 访问令牌 */
  accessToken: string;
  /** 令牌过期时间（秒） */
  expiresIn: number;
  /** 管理员信息 */
  admin: Admin;
}

/**
 * 系统维护状态响应
 */
export interface MaintenanceStatusResponse {
  /** 是否处于维护模式 */
  isMaintenance: boolean;
  /** 维护提示消息 */
  message?: string;
}

/**
 * 注册状态响应
 */
export interface RegistrationStatusResponse {
  /** 是否允许新用户注册 */
  registrationEnabled: boolean;
  /** 提示消息 */
  message?: string;
}

/**
 * 站点运行状态响应（用户端）
 */
export interface SiteStatusResponse {
  /** 系统维护模式 */
  maintenanceMode: boolean;
  /** 允许新用户注册 */
  registrationEnabled: boolean;
  /** 允许充值 */
  rechargeEnabled: boolean;
}

/**
 * 短信服务状态响应
 */
export interface SmsStatusResponse {
  /** 是否启用短信服务 */
  smsEnabled: boolean;
  /** 是否启用短信测试模式 */
  smsTestMode: boolean;
}

/**
 * 邮件服务状态响应
 */
export interface EmailStatusResponse {
  /** 是否启用邮件发送服务 */
  emailEnabled: boolean;
  /** 是否启用邮件测试模式 */
  emailTestMode: boolean;
}

/**
 * 发送验证码请求参数
 */
export interface SendVerificationCodeRequest {
  /** 手机号码 */
  phone: string;
}

/**
 * 发送验证码响应
 */
export interface SendVerificationCodeResponse {
  /** 是否发送成功 */
  success: boolean;
  /** 提示消息 */
  message: string;
  /** 测试模式下返回的验证码明文（仅短信测试模式时有值） */
  testCode?: string;
}

/**
 * 注册请求参数（扩展版，支持验证码）
 */
export interface RegisterRequest {
  /** 手机号码 */
  phone: string;
  /** 登录密码 */
  password: string;
  /** 邀请码（可选） */
  inviteCode?: string;
  /** 短信验证码（启用短信服务时必填） */
  verificationCode?: string;
}

/**
 * 忘记密码请求参数
 */
export interface ForgotPasswordRequest {
  /** 手机号码 */
  phone: string;
  /** 新密码 */
  password: string;
  /** 短信验证码（启用短信服务时必填） */
  verificationCode?: string;
}

/**
 * 站点信息响应
 */
export interface SiteInfoResponse {
  /** 公司名称 */
  companyName: string;
  /** 公司链接地址 */
  companyUrl: string;
  /** ICP 备案号 */
  icpFilingNumber: string;
  /** 备案链接地址 */
  icpFilingUrl: string;
}

/**
 * 认证 API 对象
 * 封装所有与认证相关的接口调用
 */
export const authApi = {
  /**
   * 获取站点信息（公开接口，无需授权）
   * 返回公司名称、备案号等公共信息，用于前端登录页面底部展示
   * @returns 站点信息
   */
  checkSiteInfo: (): Promise<SiteInfoResponse> =>
    http.get('/user/auth/site-info'),

  /**
   * 检查系统维护状态（公开接口，无需授权）
   * @returns 系统维护状态
   */
  checkMaintenanceStatus: (): Promise<MaintenanceStatusResponse> =>
    http.get('/user/auth/maintenance-status'),
  
  /**
   * 检查新用户注册是否开放（公开接口，无需授权）
   * @returns 注册状态
   */
  checkRegistrationStatus: (): Promise<RegistrationStatusResponse> =>
    http.get('/user/auth/registration-status'),
  
  /**
   * 获取站点运行状态（公开接口，无需授权）
   * 返回维护模式、注册开关、充值开关等状态
   * @returns 站点运行状态
   */
  checkSiteStatus: (): Promise<SiteStatusResponse> =>
    http.get('/user/auth/site-status'),
  
  /**
   * 验证邀请码是否有效（公开接口，无需授权）
   * @param inviteCode 邀请码
   * @returns 验证结果，无效时会抛出错误
   */
  validateInviteCode: (inviteCode: string): Promise<void> =>
    http.get('/user/auth/validate-invite-code', { params: { inviteCode } }),
  
  /**
   * 检查短信服务是否启用（公开接口，无需授权）
   * @returns 短信服务状态
   */
  checkSmsStatus: (): Promise<SmsStatusResponse> =>
    http.get('/user/auth/sms-status'),

  /**
   * 检查邮件服务是否启用（公开接口，无需授权）
   * @returns 邮件服务状态
   */
  checkEmailStatus: (): Promise<EmailStatusResponse> =>
    http.get('/user/auth/email-status'),
  
  /**
   * 发送短信验证码（公开接口，无需授权）
   * @param data 发送验证码请求参数（包含手机号）
   * @returns 发送结果
   */
  sendVerificationCode: (data: SendVerificationCodeRequest): Promise<SendVerificationCodeResponse> =>
    http.post('/user/auth/send-verification-code', data),
  
  /**
   * 用户登录
   * @param data 登录请求参数
   * @returns 登录响应，包含令牌和用户信息
   */
  login: (data: LoginRequest): Promise<LoginResponse> =>
    http.post('/user/auth/login', data),
  
  /**
   * 用户注册
   * @param data 注册请求参数
   * @returns 注册响应，包含令牌和用户信息
   */
  register: (data: RegisterRequest): Promise<LoginResponse> =>
    http.post('/user/auth/register', data),

  /**
   * 忘记密码
   * 通过手机号查找已有用户，更新其密码
   * @param data 忘记密码请求参数
   * @returns 重置响应，包含令牌和用户信息
   */
  forgotPassword: (data: ForgotPasswordRequest): Promise<LoginResponse> =>
    http.post('/user/auth/forgot-password', data),
  
  /**
   * 微信登录
   * @param code 微信授权码
   * @returns 登录响应
   */
  wechatLogin: (code: string): Promise<LoginResponse> =>
    http.post('/user/auth/login/wechat', { code }),
  
  /**
   * 刷新访问令牌
   * @param refreshToken 刷新令牌
   * @returns 新的访问令牌和刷新令牌
   */
  refreshToken: (refreshToken: string): Promise<{ accessToken: string; refreshToken: string }> =>
    http.post('/user/auth/refresh-token', { refreshToken }),
  
  /**
   * 管理员登录
   * @param data 管理员登录请求参数
   * @returns 管理员登录响应
   */
  adminLogin: (data: AdminLoginRequest): Promise<AdminLoginResponse> =>
    http.post('/admin/auth/login', data),
  
  /**
   * 获取当前登录用户信息
   * @returns 当前用户信息
   */
  getCurrentUser: (): Promise<User> =>
    http.get('/user/auth/me'),
  
  /**
   * 修改登录密码
   * @param oldPassword 旧密码
   * @param newPassword 新密码
   */
  changePassword: (oldPassword: string, newPassword: string): Promise<void> =>
    http.post('/user/auth/change-password', { oldPassword, newPassword }),
  
  /**
   * 更新用户邮箱（需邮件验证码校验）
   * @param email 邮箱地址
   * @param code 邮件验证码
   * @returns 更新后的用户信息
   */
  updateEmail: (email: string, code: string): Promise<User> =>
    http.put('/user/auth/update-email', { email, code }),

  /**
   * 发送邮箱验证码（需登录）
   * @param email 目标邮箱地址
   * @returns 发送结果
   */
  sendEmailCode: (email: string): Promise<SendVerificationCodeResponse> =>
    http.post('/user/auth/send-email-code', { email }),

  /**
   * 用户登出（需登录）
   * 将用户 LastActiveAt 置空，标记用户已离线
   */
  logout: (): Promise<void> =>
    http.post('/user/auth/logout'),

  /**
   * 用户登出（使用显式 token，发后即忘模式专用）
   * 不依赖 localStorage 中的 token，适合在 clearAuth 之后的场景
   * @param token 访问令牌
   */
  logoutWithToken: (token: string): Promise<void> =>
    http.post('/user/auth/logout', undefined, { headers: { Authorization: `Bearer ${token}` } }),
};
